import {buildAuthReturnLocation, buildAuthReturnQuery, createAuthCartSnapshot, normalizeAuthReturnDestination} from '~/utils/authReturn.mjs';

export function useAuthReturn(opts = {}) {
	const config = useAppConfig();
	const route = opts.route || useRoute();
	const {getAppUrl} = useApiRoutes({route});
	const enabled = config.auth?.returnContext?.enabled === true;
	const checkoutCartStorageKey = 'nuxt:auth-return:checkout-cart';
	const completedStorageKey = 'nuxt:auth-return:completed';

	function queryValue(value) {
		return Array.isArray(value) ? value[0] : value;
	}

	function normalizeDestination(value) {
		return normalizeAuthReturnDestination(value, {enabled, loginPath: getAppUrl('auth_login')});
	}

	function locationFor(path, options = {}) {
		if (typeof path !== 'string' || !path) return path;
		const destination = normalizeDestination(options.destination || route.fullPath);
		if (!destination) return path;
		if (wasRecentlyCompleted(destination)) {
			return destination.split(/[?#]/, 1)[0] === getAppUrl('auth')?.split(/[?#]/, 1)[0]
				? getAppUrl('home')
				: getAppUrl('auth');
		}

		return buildAuthReturnLocation(path, destination, options);
	}

	function location(options = {}) {
		return locationFor(getAppUrl('auth_login'), options);
	}

	function carry(path) {
		if (typeof path !== 'string' || !path) return path;
		const destination = normalizeDestination(queryValue(route.query.return_to));
		if (!destination) return path;

		const query = {
			return_to: destination,
			return_intent: queryValue(route.query.return_intent) || 'protected_page',
		};
		const actionType = queryValue(route.query.return_action_type);
		const itemCode = queryValue(route.query.return_item_code);
		if (actionType) query.return_action_type = String(actionType);
		if (itemCode) query.return_item_code = String(itemCode);

		return {path, query};
	}

	function start(options = {}) {
		if (options.intent !== 'checkout_continue') clearCheckoutCart();
		return navigateTo(location(options));
	}

	function requestPayload(fallbackUrl) {
		const payload = {success_url: fallbackUrl};
		const destination = normalizeDestination(queryValue(route.query.return_to));
		if (!destination) return payload;

		payload.return_url = destination;
		payload.return_intent = queryValue(route.query.return_intent) || 'protected_page';

		const actionType = queryValue(route.query.return_action_type);
		const itemCode = queryValue(route.query.return_item_code);
		if (actionType || itemCode) {
			payload.return_action = {};
			if (actionType) payload.return_action.type = String(actionType);
			if (itemCode) payload.return_action.item_code = String(itemCode);
		}

		return payload;
	}

	function captureCheckoutCart(cart) {
		if (!enabled || import.meta.server) return;
		const snapshot = createAuthCartSnapshot(cart);
		if (snapshot) sessionStorage.setItem(checkoutCartStorageKey, snapshot);
	}

	function clearCheckoutCart() {
		if (import.meta.client) sessionStorage.removeItem(checkoutCartStorageKey);
	}

	function consumeCheckoutCartChange(cart) {
		if (!enabled || import.meta.server) return false;
		const before = sessionStorage.getItem(checkoutCartStorageKey);
		sessionStorage.removeItem(checkoutCartStorageKey);
		if (!before) return false;
		const after = createAuthCartSnapshot(cart);
		return !!after && before !== after;
	}

	function markCompleted(destination) {
		if (!enabled || import.meta.server) return;
		const normalized = normalizeDestination(destination);
		if (!normalized) return;
		sessionStorage.setItem(completedStorageKey, JSON.stringify({destination: normalized, timestamp: Date.now()}));
	}

	function wasRecentlyCompleted(destination) {
		if (!enabled || import.meta.server) return false;
		let completed;
		try {
			completed = JSON.parse(sessionStorage.getItem(completedStorageKey));
		} catch {
			completed = null;
		}
		if (!completed || Date.now() - completed.timestamp > 30000) {
			sessionStorage.removeItem(completedStorageKey);
			return false;
		}
		return completed.destination === destination;
	}

	return {
		carry,
		captureCheckoutCart,
		clearCheckoutCart,
		consumeCheckoutCartChange,
		enabled,
		location,
		locationFor,
		markCompleted,
		normalizeDestination,
		requestPayload,
		start,
	};
}
